import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import MockAdapter from 'axios-mock-adapter';
import Fakedata from '@/api/fakedata/v1';
import DOMPurify from 'dompurify';
import { RouteUrlList, type IRouteUrlList, type IRouteUrlListElement } from '@/libs/RouteList';
import { LoadingOverLay } from '@/libs/LoadingOverLay';
import { ServiceAlert } from '@/libs/ServiceAlert';
import * as Common from '@/libs/Common';
import { SimplePopUpModalType, SimplePopUpBtnType, SimplePopUp, SimplePopUpSize } from './SimplePopUp';
const simplePopUp = new SimplePopUp();
import router from '@/router';
import { useStateErrorMessageStore } from '@/stores/modules/stateErrorMessage';

import useEsunMCA from '@/composables/useEsunMCA';

const esunMCA = useEsunMCA();

/**
 * @todo 預設API 回傳值
 */
interface GeneralResp {
    status: boolean;
    title: string;
    message: string;
    errorCode: string;
}

export type { GeneralResp };

/**
 * @todo API設定預設值
 */
export enum paramTypeEnum {
    true = 'y',
    false = 'n'
}

// 判斷WebView
const isInWebView = esunMCA.WebViewDetect();

/**
 * @todo Override Axios Instance
 */
const config = {
    headers: {
        'Content-Type': 'application/json'
    },
    defaults: {
        baseURL: import.meta.env.VITE_BaseUrl,
        timeout: import.meta.env.VITE_CallApiTimeout,
        withCredentials: true
    }
};

/**
 * @todo 更新 XSRF-Token(Cookie) And Set-Session Cookie
 */
const Instance = axios.create(config);
const overlay = new LoadingOverLay();

// ApiClient.ts 中不要立即 new，而是提供一個 getter
let _serviceAlert: ServiceAlert | null = null;

function GetServiceAlertInstance(): ServiceAlert {
    if (!_serviceAlert) {
        const isInWebView = esunMCA.WebViewDetect();
        _serviceAlert = new ServiceAlert(RouteUrlList.Index, isInWebView);
    }
    return _serviceAlert;
}

/**
 * @todo mock data
 */
if (import.meta.env.VITE_ENABLE_FAKE_DATA === 'true') {
    ((axios: AxiosInstance) => {
        const mock = new MockAdapter(axios, { delayResponse: 500 });

        mock.onAny().reply(function (config): [number, any] {
            const response = Fakedata.find(([method, path, data]) => {
                return (
                    config?.url?.match(new RegExp(`^${path}$`)) &&
                    config.method?.toUpperCase() === (method as string).toUpperCase() &&
                    (Object.keys(data).length === 0 || config?.data?.toString() === JSON.stringify(data))
                );
            });

            // Unexpected request, error out
            if (!response) {
                return [500, {}];
            }

            return response.slice(3) as [number, any];
        });
    })(Instance);
}

/**
 * @todo 自定義設定值
 */
if (import.meta.env.DEV) {
    console.table(import.meta.env);
}

Instance.defaults.baseURL = import.meta.env.VITE_WebUrl + import.meta.env.VITE_BaseUrl;
Instance.defaults.timeout = import.meta.env.VITE_CallApiTimeout;
Instance.defaults.withCredentials = true;
Common.ConsoleLog('[ApiClient] Instance.defaults.baseURL : ' + Instance.defaults.baseURL);
/**
 * @todo intercept Request
 */
Instance.interceptors.request.use(
    async (_config) => {
        Common.ConsoleLog(`[ApiClient] Request Start : method=${_config.method}; url=${_config.url}`);
        // 1.取得停止登入訊息 service alert message 預設 true(y) getServiceAlert=y
        const getServiceAlert: paramTypeEnum =
            typeof _config.params?.getServiceAlert !== 'undefined' ? _config.params?.getServiceAlert : paramTypeEnum.true;
        Common.ConsoleLog('[ApiClient] Instance.interceptors.request : getServiceAlert : ' + getServiceAlert);
        if (getServiceAlert === paramTypeEnum.true) {
            await GetServiceAlertInstance().GetAlertMessage();
        }

        // 2.bind XSCRF-token in to request headers
        let requestToken;
        if (import.meta.env.PROD) {
            const requestTokenEle = document.querySelector(`meta[name="${import.meta.env.VITE_XSRFKey_HeaderName}"]`) as HTMLMetaElement;

            if (requestTokenEle && requestTokenEle.content) {
                requestToken = requestTokenEle.content;
            }
        }
        _config.headers[import.meta.env.VITE_XSRFKey_HeaderName] = requestToken;

        // 3.是否顯示遮罩設定(輪詢ajax需能關閉遮罩)於夾帶參數中 預設 true(y) showOverlay=y
        const showOverlay: paramTypeEnum = typeof _config.params?.showOverlay !== 'undefined' ? _config.params?.showOverlay : paramTypeEnum.true;
        Common.ConsoleLog('[ApiClient] Instance.interceptors.request : showOverlay : ' + showOverlay);
        if (showOverlay === paramTypeEnum.true) {
            overlay.show(null, '');
        }

        // 4.xss filter
        // 4.1 params
        if (_config.params) {
            for (const key in _config.params) {
                if (Object.prototype.hasOwnProperty.call(_config.params, key)) {
                    // WAF will Block Request that contains SpecialChar
                    _config.params[key] = Common.ReplaceSpecialChar(
                        // DOMPurify.sanitize('<img src=x onerror=alert(1)//>'); // becomes <img src="x">
                        DOMPurify.sanitize(_config.params[key])
                    );
                }
            }
        }
        // 4.2 data
        if (_config.data) {
            for (const key in _config.data) {
                if (Object.prototype.hasOwnProperty.call(_config.data, key) && typeof _config.data[key] === 'string') {
                    // WAF will Block Request that contains SpecialChar
                    _config.data[key] = DOMPurify.sanitize(_config.data[key]);
                }
            }
        }
        return _config;
    },
    (error) => {
        overlay.hide();
        return Promise.reject(error);
    }
);
/**
 * @todo intercept Response
 */
Instance.interceptors.response.use(
    (response) => {
        Common.ConsoleLog(`[ApiClient] Response Intercept : method=${response.config.method} url=${response.config.url}`);
        overlay.hide();
        return response;
    },
    (error) => {
        // enter error condition response http code
        // 1.server timeout
        // 2.error conncetion
        // 3.server exceptions ex.blocklogin..etc
        Common.ConsoleLog(`[ApiClient] Response Intercept error` + error);
        overlay.hide();
        return Promise.reject(error);
    }
);

/**
 * @todo POST Function (停登訊息確認後，顯示API回應)
 * @param url
 * @param data
 * @returns
 */
export async function post(url: string, data: object = {}, config: object = {}): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
        Instance.post(url, data, config)
            .then(async (response) => {
                //1.閒置逾時偵測
                const skipIdletimeout: paramTypeEnum =
                    typeof response.config.params?.skipIdletimeout !== 'undefined' ? response.config.params?.skipIdletimeout : paramTypeEnum.false;
                Common.ConsoleLog('[ApiClient] Request Post() isSkipTimeoutDetection: ' + skipIdletimeout);
                Common.ConsoleLog('[ApiClient] Request Post() Get Response Start: ' + url);

                if (skipIdletimeout !== paramTypeEnum.true) {
                    Common.ConsoleLog('[ApiClient] Request Post() Get Response Start serviceAlert.IdletimeoutDetection');
                    await GetServiceAlertInstance().IdletimeoutDetection();
                }
                //2.顯示停登彈窗
                //3.當此次請求網址是確認閱讀service alert message，不顯示彈窗訊息
                const isDisplay: boolean = !(response.config.url === RouteUrlList.API_ConfirmServiceAlert.path);
                await GetServiceAlertInstance()
                    .ShowAlertMessage(isDisplay)
                    .then((res) => {
                        Common.ConsoleLog('[ApiClient-ShowAlertMessage] Request Post() Get Response End: ' + url);
                        resolve(response);
                    });
            })
            .catch((error) => {
                Common.ConsoleLog('[ApiClient] Request Post() Get Response End: ' + url);
                reject(error);
            });
    });
}

/**
 * @todo 停登訊息確認後，顯示API回應
 * @param url
 * @param data
 * @returns
 */
export async function get(url: string, config: object = {}): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
        Instance.get(url, config)
            .then(async (response) => {
                Common.ConsoleLog('[ApiClient] Get() Get Response : ' + url);
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            });
    });
}

const defaultErrorMsg = {
    busy: '系統忙碌中 請稍候再試',
    unknown: '系統錯誤，請重新整理頁面',
    disconnect: '資料連線中斷，請回起始頁'
};

/**
 * @todo 處理 回應 200但，狀態status為false
 */
const HandleGeneralRespError = (res: any): Promise<any> => {
    const msg = res && res.message ? res.message : defaultErrorMsg.busy;
    return simplePopUp.showAlert(SimplePopUpModalType.Warning, '錯誤', msg, [
        {
            id: 'confirm',
            type: SimplePopUpBtnType.callback,
            btnClass: 'btn-info',
            resolveParam: 'confirm',
            title: '確認'
        }
    ]);
};

/**
 * ErrorCode [http 400] API 錯誤處理
 * @param {XMLHttpRequest} error
 */
const HandleError = (
    error: any | AxiosResponse<any>,
    isRedirect: boolean = false,
    redirectPath: string | null = import.meta.env.VITE_BaseUrl + RouteUrlList.Login.path,
    isUseRoutePush: boolean = false,
    routeName: IRouteUrlListElement = RouteUrlList.Login
): Promise<any> => {
    const defaultErrorTitle: string = '錯誤';
    const defaultConfirmText: string = '確認';

    if (error && error.response) {
        const ErrorCase =
            error.response.data && error.response.data.errorCode ? parseInt(error.response.data.errorCode.replace('ER', '')) : ErrorCode.Unknown;
        const title: string = error.response.data.title || defaultErrorTitle;
        const msg: string = error.response.data.message || defaultErrorMsg.unknown;
        Common.ConsoleLog(title + msg);
        const isIniFrame = window === window.parent ? false : true;

        const errorCode = 'ER' + ErrorCase.toString().padStart(3, '0');
        // Store Message in pinia
        try {
            const stateErrorMessageStore = useStateErrorMessageStore();
            // Example of setting the state
            stateErrorMessageStore.setStateErrorMessage({
                title: title,
                message: msg,
                errorCode: errorCode
            });
        } catch (ex) {
            Common.ConsoleLog(ex);
        }
        // Start Handle Error
        if (error.response.status === 401) {
            if (isInWebView) {
                esunMCA.SendMessage(0, '401 驗證失敗');
            }
            return simplePopUp
                .showAlert(
                    SimplePopUpModalType.Warning,
                    title,
                    '驗證失敗',
                    [
                        {
                            id: 'confirm',
                            type: SimplePopUpBtnType.callback,
                            btnClass: 'btn-info',
                            resolveParam: 'confirm',
                            title: defaultConfirmText
                        }
                    ],
                    SimplePopUpSize.lg,
                    true
                )
                .then(() => {
                    router.replace({
                        name: RouteUrlList.StateError.name,
                        query: { readlocally: 'y' }
                    });
                });
        } else if (error.response.status === 403) {
            if (isInWebView) {
                esunMCA.SendMessage(0, '403 未授權');
            }
            return simplePopUp.showAlert(SimplePopUpModalType.Warning, title, '未授權', [], SimplePopUpSize.lg, true).then(() => {
                router.replace({
                    name: RouteUrlList.StateError.name,
                    query: { readlocally: 'y' }
                });
            });
        } else if (ErrorCase === ErrorCode.ServiceMaintaining) {
            // Service Maintaining
            if (isInWebView) {
                esunMCA.SendMessage(0, msg);
            }
            return simplePopUp
                .showAlert(SimplePopUpModalType.Warning, title, `${msg}`, [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn-info',
                        resolveParam: 'confirm',
                        title: defaultConfirmText
                    }
                ])
                .then(() => {
                    router.replace({
                        name: RouteUrlList.StateError.name,
                        query: { readlocally: 'y' }
                    });
                });
        } else if (ErrorCase === ErrorCode.SessionExpired || ErrorCase === ErrorCode.IllegalFlow) {
            if (isInWebView) {
                esunMCA.SendMessage(0, msg);
            }
            return simplePopUp
                .showAlert(SimplePopUpModalType.Warning, title, `${errorCode} ${msg}`, [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn-info',
                        resolveParam: 'confirm',
                        title: defaultConfirmText
                    }
                ])
                .then(() => {
                    if (isIniFrame) {
                        parent.location.reload();
                    } else {
                        if (isRedirect && redirectPath) {
                            window.location.href = redirectPath;
                        } else {
                            router.replace({
                                name: RouteUrlList.StateError.name,
                                query: { readlocally: 'y' }
                            });
                        }
                    }
                });
        } else if (ErrorCase === ErrorCode.RedirectToFinishPage) {
            // 已完成申請 您已完成申請，將為您跳轉至申請進度查詢頁面
            return new Promise((resolve, reject) => {
                router.replace({
                    name: RouteUrlList.StatusOfApply.name,
                    query: { readlocally: 'y' }
                });
                // window.location.href = import.meta.env.VITE_BaseUrl + RouteUrlList.StatusOfApply.path;
            });
        } else if (ErrorCase === ErrorCode.SkipContractPage) {
            // 已簽署契約 您已簽署部分契約，將為您跳轉至下一階段契約書頁面。
            return new Promise((resolve, reject) => {
                router.replace({
                    name: RouteUrlList.RiskWarningContract.name,
                    query: { readlocally: 'y' }
                });
            });
        } else if (ErrorCase === ErrorCode.SkipMarginContractPage) {
            // 已簽署契約 您已簽署融資融券契約書，將為您跳轉至契約確認頁面。
            return new Promise((resolve, reject) => {
                router.replace({
                    name: RouteUrlList.ContractConfirm.name,
                    query: { readlocally: 'y' }
                });
            });
        } else if (ErrorCase === ErrorCode.SkipConfirmContractPage) {
            // 已簽署契約 您已確認期貨開戶契約書，將為您跳轉至身分證件上傳頁面。
            return new Promise((resolve, reject) => {
                router.replace({
                    name: RouteUrlList.IdDocUpload.name,
                    query: { readlocally: 'y' }
                });
            });
        } else if (ErrorCase === ErrorCode.DayLimitReached) {
            // 很抱歉，您的簡訊/Email驗證碼發送次數已超過每日上線，請於隔日凌晨00:00後再進行線上開戶，造成不便敬請見諒。
            if (isInWebView) {
                esunMCA.SendMessage(0, msg);
            }
            return new Promise((resolve, reject) => {
                router.replace({
                    name: RouteUrlList.StateError.name,
                    query: { readlocally: 'y' }
                });
            });
        } else {
            // 其他 Error Code
            return simplePopUp
                .showAlert(SimplePopUpModalType.Warning, title, `${errorCode} ${msg}`, [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn-info',
                        resolveParam: 'confirm',
                        title: defaultConfirmText
                    }
                ])
                .then(() => {
                    if (isRedirect && redirectPath) {
                        window.location.href = redirectPath;
                    } else if (isUseRoutePush) {
                        router.replace({ name: routeName.name, query: { readlocally: 'y' } });
                    }
                });
        }
    } else if (error && error.request) {
        // The request was made and the server responded with a status code
        return simplePopUp
            .showAlert(SimplePopUpModalType.Warning, defaultErrorTitle, defaultErrorMsg.disconnect, [
                {
                    id: 'confirm',
                    type: SimplePopUpBtnType.callback,
                    btnClass: 'btn-info',
                    resolveParam: 'confirm',
                    title: defaultConfirmText
                }
            ])
            .then(() => {
                if (isRedirect && redirectPath) {
                    window.location.href = redirectPath;
                } else if (isUseRoutePush) {
                    router.replace({ name: routeName.name, query: { readlocally: 'y' } });
                }
            });
    } else {
        // no response
        if (isInWebView) {
            esunMCA.SendMessage(0, defaultErrorMsg.disconnect);
        }
        return simplePopUp
            .showAlert(SimplePopUpModalType.Warning, defaultErrorTitle, defaultErrorMsg.unknown, [
                {
                    id: 'confirm',
                    type: SimplePopUpBtnType.callback,
                    btnClass: 'btn-info',
                    resolveParam: 'confirm',
                    title: defaultConfirmText
                }
            ])
            .then(() => {
                if (isRedirect && redirectPath) {
                    window.location.href = redirectPath;
                } else if (isUseRoutePush) {
                    router.replace({ name: routeName.name, query: { readlocally: 'y' } });
                }
            });
    }
};

/**
 * 錯誤代碼清單
 */
enum ErrorCode {
    ServiceMaintaining = 0,
    GetServiceAlertError = 1,
    ModelBindingError = 2,
    HttpError = 3,
    TaskCanceledError = 4,
    Unknown = 5,
    PayloadTooLargeError = 6,
    GetClientIPError = 7,
    TokenExpired = 8, // 很抱歉，申請作業已逾時，請重新登入，造成不便敬請見諒。
    SessionExpired = 9, // 很抱歉，申請作業已逾時，請重新登入，造成不便敬請見諒。
    H5VerifyPkcs7Error = 10, // 您的風險等級無法申購此商品
    EsunMCAVerifyError = 11,
    SSOLoginGetSkeyError = 12,
    SSOLoginError = 13,
    GetSkeyForApplyCertError = 14,
    HasMultiAccount = 15,
    SourceNotFoundError = 16,
    CaptchaError = 17,
    GetBranchError = 18,
    GetCountryError = 19,
    GetCityError = 20,
    GetAreaError = 21,
    MOI_Error = 23,
    GetJobError = 24,
    GetOccupationError = 25,
    GetJobTitleParentError = 26,
    GetJobTitleError = 27,
    OtpApiError = 28,
    GetStockCustomerDataError = 29,
    GetContractAPIError = 30,
    GetMOISiteError = 31,
    CheckLoginError = 32,
    OauthLoginError = 33,
    NonAccountError = 34,
    IllegalFlow = 35,
    DayLimitReached = 36,

    RedirectToFinishPage = 112,
    SkipContractPage = 113,
    SkipMarginContractPage = 114,
    SkipConfirmContractPage = 117
}

/**
 * 模擬延遲後執行 callback
 * @param callback 要執行的函式（可為同步或非同步）
 * @param ms 延遲毫秒
 * @returns callback 執行結果（支援 Promise）
 */
const Delay = async <T>(callback: () => T | Promise<T>, ms: number): Promise<T> => {
    await new Promise<void>((resolve) => setTimeout(resolve, ms));
    return await callback();
};

export { config, Instance, defaultErrorMsg, HandleGeneralRespError, HandleError, GetServiceAlertInstance, ErrorCode, Delay };
